import { subscribeWithSelector } from "zustand/middleware";
import { createWithEqualityFn } from "zustand/traditional";

import { type CanvasActions, type CanvasState, createCanvasSlice, initialCanvasState } from "./slices/canvas-slice.ts";
import { createMainSlice, initialMainState, type MainActions, type MainState } from "./slices/main-slice.ts";
import {
  createRenderingSlice,
  initialRenderingState,
  type RenderingActions,
  type RenderingState,
} from "./slices/rendering-slice.ts";
import {
  createSaveLoadSlice,
  initialSaveLoadState,
  type SaveLoadActions,
  type SaveLoadState,
} from "./slices/save-load-slice.ts";
import { createSearchSlice, initialSearchState, type SearchActions, type SearchState } from "./slices/search-slice.ts";
import {
  createSelectionSlice,
  initialSelectionState,
  type SelectionActions,
  type SelectionState,
} from "./slices/selection-slice.ts";
import { createTracksSlice, initialTracksState, type TracksActions, type TracksState } from "./slices/tracks-slice.ts";
import {
  createUserEventsSlice,
  initialUserEventsState,
  type UserEventsActions,
  type UserEventsState,
} from "./slices/user-events-slice.ts";

export type GenomeViewerState = MainState &
  CanvasState &
  TracksState &
  UserEventsState &
  RenderingState &
  SelectionState &
  SaveLoadState &
  SearchState;

export type GenomeViewerActions = MainActions &
  CanvasActions &
  TracksActions &
  UserEventsActions &
  RenderingActions &
  SelectionActions &
  SaveLoadActions &
  SearchActions;

export type GenomeViewerStore = GenomeViewerState & GenomeViewerActions;

export const resetState: Partial<GenomeViewerState> = {
  ...initialMainState,
  ...initialCanvasState,
  ...initialTracksState,
  ...initialUserEventsState,
  ...initialRenderingState,
  ...initialSelectionState,
  ...initialSaveLoadState,
  ...initialSearchState,
};

export const useGenomeViewerStore = createWithEqualityFn(
  subscribeWithSelector<GenomeViewerStore>((set, get, store) => ({
    ...createMainSlice(set, get, store),
    ...createCanvasSlice(set, get, store),
    ...createTracksSlice(set, get, store),
    ...createUserEventsSlice(set, get, store),
    ...createRenderingSlice(set, get, store),
    ...createSelectionSlice(set, get, store),
    ...createSaveLoadSlice(set, get, store),
    ...createSearchSlice(set, get, store),
  })),
);
